import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';

type NodeStep = 'observe' | 'reflect' | 'practice' | 'reveal' | 'treasure' | 'complete';

export default function LivingNode() {
  const { t } = useTranslation();
  const [step, setStep] = useState<NodeStep>('observe');
  const [observeInput, setObserveInput] = useState('');
  const [reflectInput, setReflectInput] = useState('');
  const [practiceDone, setPracticeDone] = useState(false);
  const [revealInput, setRevealInput] = useState('');
  const [treasureName, setTreasureName] = useState('');

  const canAdvance = useCallback((currentStep: NodeStep): boolean => {
    switch (currentStep) {
      case 'observe': return observeInput.trim().length > 0;
      case 'reflect': return reflectInput.trim().length > 0;
      case 'practice': return practiceDone;
      case 'reveal': return revealInput.trim().length > 0;
      case 'treasure': return treasureName.trim().length > 0;
      default: return true;
    }
  }, [observeInput, reflectInput, practiceDone, revealInput, treasureName]);

  const handleNext = useCallback(() => {
    const flow: Record<NodeStep, NodeStep> = {
      observe: 'reflect',
      reflect: 'practice',
      practice: 'reveal',
      reveal: 'treasure',
      treasure: 'complete',
      complete: 'complete',
    };
    setStep(flow[step]);
  }, [step]);

  const steps: { key: NodeStep; order: number }[] = [
    { key: 'observe', order: 1 },
    { key: 'reflect', order: 2 },
    { key: 'practice', order: 3 },
    { key: 'reveal', order: 4 },
    { key: 'treasure', order: 5 },
  ];

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />

      {/* Page header */}
      <div className="pt-20 md:pt-24 pb-6 md:pb-8 text-center bg-background-50">
        <div className="flex items-center justify-center gap-2 mb-3">
          <AngelIcon variant="gold" size="md" />
        </div>
        <h1
          className="text-2xl md:text-3xl text-foreground-800 mb-1"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t('livingNode.title')}
        </h1>
        <p className="text-xs text-foreground-400 uppercase tracking-[0.15em]">
          {t('livingNode.titleEn')}
        </p>
        <p className="text-sm text-foreground-500 mt-1 italic">
          {t('livingNode.subtitle')}
        </p>
        <p className="text-xs text-foreground-400">
          {t('livingNode.subtitleEn')}
        </p>

        {/* Current node identity */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 md:gap-4 mt-5">
          <div className="flex items-center gap-2 bg-background-100 rounded-xl px-4 py-2 border border-foreground-200/40">
            <span className="text-[10px] text-foreground-400 tracking-wider">{t('livingNode.current.journey')}</span>
            <span className="text-xs font-medium text-foreground-700">{t('livingNode.current.journey')}</span>
          </div>
          <i className="ri-arrow-right-line text-foreground-300 hidden sm:block" />
          <div className="flex items-center gap-2 bg-background-100 rounded-xl px-4 py-2 border border-foreground-200/40">
            <span className="text-[10px] text-foreground-400 tracking-wider">{t('journey.currentPosition.root')}</span>
            <span className="text-xs font-medium text-foreground-700">{t('livingNode.current.root')}</span>
            <span className="text-[10px] text-foreground-400">{t('livingNode.current.rootEn')}</span>
          </div>
          <i className="ri-arrow-right-line text-foreground-300 hidden sm:block" />
          <div className="flex items-center gap-2 bg-primary-50 rounded-xl px-4 py-2 border border-primary-200/50">
            <div className="w-6 h-6 rounded-full bg-primary-200 flex items-center justify-center">
              <span className="text-primary-700 text-[10px] font-semibold">3</span>
            </div>
            <span className="text-xs font-medium text-primary-800">{t('livingNode.current.nodeName')}</span>
            <span className="text-[10px] text-primary-500">{t('livingNode.current.nodeNameEn')}</span>
          </div>
        </div>
      </div>

      {/* Step progress indicator */}
      <div className="max-w-2xl mx-auto px-4 md:px-6 mb-8">
        <div className="flex items-center justify-center gap-1 md:gap-2">
          {steps.map((s, i) => {
            const isActive = step === s.key;
            const isDone = steps.findIndex((st) => st.key === step) > i;
            const isTreasure = s.key === 'treasure';
            return (
              <div key={s.key} className="flex items-center gap-1 md:gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : isDone
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-foreground-100 text-foreground-400'
                  }`}
                >
                  {isTreasure ? (
                    <i className={`${isActive ? 'ri-gift-line' : isDone ? 'ri-gift-line' : 'ri-gift-line'} text-xs`} />
                  ) : (
                    s.order
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-4 md:w-6 h-px ${isDone ? 'bg-primary-300' : 'bg-foreground-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 md:px-6 pb-20">
        {/* ── Step 1: Observe ─────────────────────────── */}
        {step === 'observe' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-eye-line text-foreground-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('livingNode.steps.observe.title')}
                </h3>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
                  {t('livingNode.steps.observe.titleEn')}
                </p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 mb-1">
              {t('livingNode.steps.observe.prompt')}
            </p>
            <p className="text-xs text-foreground-400 mb-4">
              {t('livingNode.steps.observe.promptEn')}
            </p>

            <textarea
              value={observeInput}
              onChange={(e) => setObserveInput(e.target.value)}
              maxLength={300}
              rows={4}
              className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
              placeholder={t('livingNode.steps.observe.placeholder')}
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">
              {observeInput.length} / 300
            </p>

            <div className="flex justify-end mt-4">
              <button
                onClick={handleNext}
                disabled={!canAdvance('observe')}
                className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('livingNode.steps.reflect.title')}
                <i className="ri-arrow-right-line ml-1.5" />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Reflect ──────────────────────────── */}
        {step === 'reflect' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                <i className="ri-pause-circle-line text-primary-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('livingNode.steps.reflect.title')}
                </h3>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
                  {t('livingNode.steps.reflect.titleEn')}
                </p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 mb-1">
              {t('livingNode.steps.reflect.prompt')}
            </p>
            <p className="text-xs text-foreground-400 mb-5">
              {t('livingNode.steps.reflect.promptEn')}
            </p>

            {/* AI Angel asks three questions */}
            <div className="space-y-3 mb-5">
              {[1, 2, 3].map((q) => (
                <div key={q} className="bg-background-50 border border-foreground-200/30 rounded-lg px-4 py-3">
                  <div className="flex items-start gap-2">
                    <i className="ri-question-line text-primary-400 text-sm mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-foreground-700">
                        {t(`livingNode.steps.reflect.q${q}` as any)}
                      </p>
                      <p className="text-[10px] text-foreground-400">
                        {t(`livingNode.steps.reflect.q${q}En` as any)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <textarea
              value={reflectInput}
              onChange={(e) => setReflectInput(e.target.value)}
              maxLength={500}
              rows={5}
              className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
              placeholder={t('livingNode.steps.reflect.placeholder')}
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">
              {reflectInput.length} / 500
            </p>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setStep('observe')}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" />
                {t('livingNode.steps.observe.title')}
              </button>
              <button
                onClick={handleNext}
                disabled={!canAdvance('reflect')}
                className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('livingNode.steps.practice.title')}
                <i className="ri-arrow-right-line ml-1.5" />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Practice ─────────────────────────── */}
        {step === 'practice' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-accent-50 flex items-center justify-center">
                <i className="ri-riding-line text-accent-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('livingNode.steps.practice.title')}
                </h3>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
                  {t('livingNode.steps.practice.titleEn')}
                </p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 mb-1">
              {t('livingNode.steps.practice.prompt')}
            </p>
            <p className="text-xs text-foreground-400 mb-5">
              {t('livingNode.steps.practice.promptEn')}
            </p>

            {/* Practice card — the actual action */}
            <div className="bg-accent-50 border border-accent-200/40 rounded-xl p-5 text-center mb-6">
              <AngelIcon variant="gold" size="md" />
              <p className="text-sm font-medium text-foreground-800 mt-3 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('livingNode.steps.practice.action')}
              </p>
              <p className="text-xs text-foreground-500">
                {t('livingNode.steps.practice.actionEn')}
              </p>
            </div>

            {/* Practice confirmation */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => { setPracticeDone(true); }}
                className={`px-5 py-2.5 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  practiceDone
                    ? 'bg-accent-500 text-background-50'
                    : 'border-2 border-accent-300 text-accent-600 hover:bg-accent-50'
                }`}
              >
                <i className={practiceDone ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} />
                {t('livingNode.steps.practice.done')}
              </button>
              <button
                onClick={() => { setPracticeDone(false); }}
                className={`px-5 py-2.5 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                  !practiceDone && practiceDone === false
                    ? ''
                    : 'border border-foreground-200 text-foreground-500 hover:bg-foreground-50'
                }`}
              >
                {t('livingNode.steps.practice.notYet')}
              </button>
            </div>
            {!practiceDone && (
              <p className="text-center text-xs text-foreground-400 mt-3">
                {t('livingNode.steps.practice.notYetEn')}
              </p>
            )}

            <div className="flex items-center justify-between mt-5">
              <button
                onClick={() => setStep('reflect')}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" />
                {t('livingNode.steps.reflect.title')}
              </button>
              <button
                onClick={handleNext}
                disabled={!canAdvance('practice')}
                className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('livingNode.steps.reveal.title')}
                <i className="ri-arrow-right-line ml-1.5" />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Reveal ────────────────────────────── */}
        {step === 'reveal' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-secondary-50 flex items-center justify-center relative">
                <i className="ri-sparkling-line text-secondary-500" />
                <div className="absolute inset-0 rounded-full bg-secondary-100/30 animate-pulse" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('livingNode.steps.reveal.title')}
                </h3>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider">
                  {t('livingNode.steps.reveal.titleEn')}
                </p>
              </div>
            </div>

            <p className="text-sm text-foreground-600 mb-1">
              {t('livingNode.steps.reveal.prompt')}
            </p>
            <p className="text-xs text-foreground-400 mb-1">
              {t('livingNode.steps.reveal.promptEn')}
            </p>
            <p className="text-[11px] text-foreground-300 italic mb-4">
              {t('livingNode.steps.reveal.hint')}
            </p>
            <p className="text-[10px] text-foreground-300 italic mb-5">
              {t('livingNode.steps.reveal.hintEn')}
            </p>

            <textarea
              value={revealInput}
              onChange={(e) => setRevealInput(e.target.value)}
              maxLength={300}
              rows={4}
              className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-secondary-300 resize-none"
              placeholder={t('livingNode.steps.reveal.placeholder')}
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">
              {revealInput.length} / 300
            </p>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setStep('practice')}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" />
                {t('livingNode.steps.practice.title')}
              </button>
              <button
                onClick={handleNext}
                disabled={!canAdvance('reveal')}
                className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-1.5" />
                {t('livingNode.generateTreasure.title')}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: Generate Treasure ─────────────────── */}
        {step === 'treasure' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-gift-line text-amber-400 text-2xl" />
            </div>
            <h3 className="text-sm font-medium text-foreground-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('livingNode.generateTreasure.title')}
            </h3>
            <p className="text-xs text-foreground-400 uppercase tracking-wider">
              {t('livingNode.generateTreasure.titleEn')}
            </p>
            <p className="text-xs text-foreground-500 mt-3 leading-relaxed max-w-md mx-auto">
              {t('livingNode.generateTreasure.prompt')}
            </p>
            <p className="text-[10px] text-foreground-400 mb-5">
              {t('livingNode.generateTreasure.promptEn')}
            </p>

            <div className="max-w-xs mx-auto mb-5">
              <p className="text-xs text-foreground-400 mb-2 text-left">
                {t('livingNode.generateTreasure.treasureName')}
              </p>
              <input
                type="text"
                value={treasureName}
                onChange={(e) => setTreasureName(e.target.value)}
                maxLength={50}
                className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300 text-center"
                placeholder={t('livingNode.generateTreasure.treasureNameEn')}
              />
            </div>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setStep('reveal')}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" />
                {t('livingNode.steps.reveal.title')}
              </button>
              <button
                onClick={handleNext}
                disabled={!canAdvance('treasure')}
                className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-check-line" />
                {t('livingNode.generateTreasure.button')}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 6: Complete ──────────────────────────── */}
        {step === 'complete' && (
          <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <AngelIcon variant="gold" size="lg" />
            </div>
            <h3 className="text-sm font-medium text-foreground-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('livingNode.completion.title')}
            </h3>
            <p className="text-xs text-foreground-400 uppercase tracking-wider">
              {t('livingNode.completion.titleEn')}
            </p>
            <p className="text-xs text-foreground-500 mt-3 leading-relaxed max-w-md mx-auto mb-1">
              {t('livingNode.completion.message')}
            </p>
            <p className="text-[10px] text-foreground-400 max-w-md mx-auto leading-relaxed mb-6">
              {t('livingNode.completion.messageEn')}
            </p>

            {/* Treasure collected badge */}
            <div className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200/50 rounded-xl px-4 py-3 mb-6">
              <i className="ri-check-double-line text-primary-500 text-lg" />
              <div className="text-left">
                <p className="text-xs text-primary-700">
                  {t('livingNode.generateTreasure.added')}: <span className="font-medium">{treasureName}</span>
                </p>
                <p className="text-[10px] text-primary-500">{t('livingNode.generateTreasure.addedEn')}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/evidence"
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-archive-line" />
                {t('livingNode.generateTreasure.viewTreasures')}
              </Link>
              <Link
                to="/journey"
                className="px-5 py-2.5 rounded-md border border-foreground-200 text-foreground-600 text-sm hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-compass-3-line" />
                {t('livingNode.completion.backToDashboard')}
              </Link>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}